import React, { useEffect, useState } from 'react'
import {
  Box, Typography, TextField, Button, Checkbox, FormControlLabel,
  FormGroup, CircularProgress, Paper,
} from '@mui/material'
import { generateClient } from '@aws-amplify/api'
import { LabelStack } from '../../Components/LabelStack'
import {
  buildBusinessVerificationQuery,
  buildWorkflowVerificationQuery,
  getBusinessVerificationDomains,
  loadDevQueryTypeMap,
} from './graphqlSchema'

const client = generateClient()

const executeGraphql = async (query, variables) => {
  const result = await client.graphql({
    query,
    variables,
    authMode: 'userPool',
  })
  return { data: result.data }
}

const extractBevLabels = (result, domains) => {
  const data = result?.data?.getBusinessVerificationByDomains
  if (!data) return null

  const orderLabels = data.labels || []
  const domainEntries = domains
    .filter((d) => data[d] && data[d].domainLabels?.length)
    .map((d) => ({ domain: d, labels: data[d].domainLabels }))

  return { orderLabels, domainEntries }
}

const BevLabelSummary = ({ result, domains }) => {
  const summary = extractBevLabels(result, domains)
  if (!summary) return null

  const { orderLabels, domainEntries } = summary
  if (!orderLabels.length && !domainEntries.length) return null

  return (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: 'background.paper' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Label Summary</Typography>
      {orderLabels.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Order:
          </Typography>
          <LabelStack labels={orderLabels} />
        </Box>
      )}
      {domainEntries.map(({ domain, labels }) => (
        <Box key={domain} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 200 }}>
            {domain}:
          </Typography>
          <LabelStack labels={labels} />
        </Box>
      ))}
    </Paper>
  )
}

const ResultPanel = ({ title, loading, error, result, labelSummary }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
    {loading && <CircularProgress size={24} />}
    {error && (
      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#fdecea' }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    )}
    {labelSummary}
    {result && (
      <Paper sx={{ p: 2 }}>
        <Box
          component="pre"
          sx={{
            overflow: 'auto',
            maxHeight: '70vh',
            fontSize: '0.8rem',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {JSON.stringify(result, null, 2)}
        </Box>
      </Paper>
    )}
  </Box>
)

const DevQueryTool = () => {
  const [orderId, setOrderId] = useState('')
  const [availableDomains, setAvailableDomains] = useState([])
  const [selectedDomains, setSelectedDomains] = useState([])
  const [runBev, setRunBev] = useState(true)
  const [runWv, setRunWv] = useState(true)
  const [typeMap, setTypeMap] = useState(null)
  const [schemaLoading, setSchemaLoading] = useState(true)
  const [schemaError, setSchemaError] = useState(null)

  const [bevLoading, setBevLoading] = useState(false)
  const [bevResult, setBevResult] = useState(null)
  const [bevError, setBevError] = useState(null)

  const [wvLoading, setWvLoading] = useState(false)
  const [wvResult, setWvResult] = useState(null)
  const [wvError, setWvError] = useState(null)

  useEffect(() => {
    let isMounted = true

    const hydrateSchema = async () => {
      setSchemaLoading(true)
      setSchemaError(null)

      try {
        const nextTypeMap = await loadDevQueryTypeMap(executeGraphql)
        const nextDomains = getBusinessVerificationDomains(nextTypeMap)

        if (!isMounted) {
          return
        }

        setTypeMap(nextTypeMap)
        setAvailableDomains(nextDomains)
        setSelectedDomains((currentDomains) => {
          const preservedDomains = currentDomains.filter((domain) => nextDomains.includes(domain))
          return preservedDomains.length > 0 ? preservedDomains : [...nextDomains]
        })
      } catch (err) {
        if (!isMounted) {
          return
        }

        setSchemaError(err?.message || 'Unable to load GraphQL schema introspection')
      } finally {
        if (isMounted) {
          setSchemaLoading(false)
        }
      }
    }

    hydrateSchema()

    return () => {
      isMounted = false
    }
  }, [])

  const toggleDomain = (domain) => {
    setSelectedDomains((prev) =>
      prev.includes(domain)
        ? prev.filter((d) => d !== domain)
        : [...prev, domain],
    )
  }

  const selectAll = () => setSelectedDomains([...availableDomains])
  const selectNone = () => setSelectedDomains([])

  const runQuery = async () => {
    if (!orderId.trim() || !typeMap) return
    const variables = { orderId: orderId.trim() }

    if (runBev) {
      const bevQuery = buildBusinessVerificationQuery({ typeMap, selectedDomains })
      setBevLoading(true)
      setBevResult(null)
      setBevError(null)
      executeGraphql(bevQuery, variables)
        .then(setBevResult)
        .catch((err) => setBevError(err?.message || err?.errors?.[0]?.message || 'Unknown error'))
        .finally(() => setBevLoading(false))
    }

    if (runWv) {
      const workflowQuery = buildWorkflowVerificationQuery({ typeMap })
      setWvLoading(true)
      setWvResult(null)
      setWvError(null)
      executeGraphql(workflowQuery, variables)
        .then(setWvResult)
        .catch((err) => setWvError(err?.message || err?.errors?.[0]?.message || 'Unknown error'))
        .finally(() => setWvLoading(false))
    }
  }

  const loading = schemaLoading || bevLoading || wvLoading

  return (
    <Box padding="1.5rem 2.5rem" sx={{ backgroundColor: 'background.main', minHeight: '100%' }}>
      <Typography variant="h1" color="emphasis.dark">
        BEV Domain Query Tool
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        Dev tool to test getBusinessVerificationByDomains and getWorkflowVerification GraphQL queries
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <TextField
          label="Order ID"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          size="small"
          sx={{ width: 400 }}
          onKeyDown={(e) => e.key === 'Enter' && !loading && typeMap && orderId.trim() && (runBev || runWv) && runQuery()}
        />
        <Button variant="contained" onClick={runQuery} disabled={loading || !typeMap || !orderId.trim() || (!runBev && !runWv)}>
          {loading ? <CircularProgress size={20} /> : 'Run Query'}
        </Button>
        <FormControlLabel
          control={<Checkbox checked={runBev} onChange={() => setRunBev(!runBev)} size="small" />}
          label="BEV Domains"
        />
        <FormControlLabel
          control={<Checkbox checked={runWv} onChange={() => setRunWv(!runWv)} size="small" />}
          label="Workflow Verification"
        />
      </Box>

      {schemaError && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#fdecea' }}>
          <Typography color="error">{schemaError}</Typography>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h6">Domains</Typography>
          <Button size="small" onClick={selectAll}>All</Button>
          <Button size="small" onClick={selectNone}>None</Button>
        </Box>
        {schemaLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading GraphQL schema introspection...
            </Typography>
          </Box>
        ) : (
          <FormGroup row>
            {availableDomains.map((domain) => (
              <FormControlLabel
                key={domain}
                control={
                  <Checkbox
                    checked={selectedDomains.includes(domain)}
                    onChange={() => toggleDomain(domain)}
                    size="small"
                  />
                }
                label={domain}
                sx={{ minWidth: 220 }}
              />
            ))}
          </FormGroup>
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {runBev && (
          <ResultPanel
            title="getBusinessVerificationByDomains"
            loading={bevLoading}
            error={bevError}
            result={bevResult}
            labelSummary={bevResult && <BevLabelSummary result={bevResult} domains={availableDomains} />}
          />
        )}
        {runWv && (
          <ResultPanel
            title="getWorkflowVerification"
            loading={wvLoading}
            error={wvError}
            result={wvResult}
          />
        )}
      </Box>
    </Box>
  )
}

export default DevQueryTool
